import React, { useState, useEffect, useCallback } from 'react';
import { 
  Ticket, Clock, CheckCircle, AlertCircle, 
  MessageSquare, Send, RefreshCw, Filter, Search,
  Bug, Mic, Monitor, Lightbulb, HelpCircle, Loader2
} from 'lucide-react';
import { MainLayout } from '../components/layout/MainLayout';
import { useTickets } from '../context/TicketContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500', textColor: 'text-blue-400', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-amber-500', textColor: 'text-amber-400', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-emerald-500', textColor: 'text-emerald-400', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-500', textColor: 'text-gray-400', icon: CheckCircle },
};

const CATEGORY_ICONS = {
  bug: Bug,
  voice: Mic,
  ui: Monitor,
  feature: Lightbulb,
  other: HelpCircle,
  general: HelpCircle,
  network: AlertCircle,
};

const MyTickets = () => {
  const { fetchUserTickets, respondToTicket } = useTickets();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchUserTickets();
      setTickets(data.tickets || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [fetchUserTickets]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleSendResponse = async () => {
    if (!newResponse.trim() || !selectedTicket) return;
    
    setSubmitting(true);
    try {
      await respondToTicket(selectedTicket.ticket_id, 'Follow-up', newResponse);
      toast.success('Response sent');
      setNewResponse('');
      const response = await axios.get(
        `${API_URL}/api/tickets/my-tickets/${selectedTicket.ticket_id}`,
        { withCredentials: true }
      );
      setSelectedTicket(response.data);
      loadTickets();
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesSearch = !searchQuery || 
      ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.ticket_id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="my-tickets-title">
                <Ticket className="h-6 w-6 text-blue-400" />
                My Support Tickets
              </h1>
              <p className="text-muted-foreground text-sm">Track and manage your reported issues</p>
            </div>
            <button
              onClick={loadTickets}
              className="p-2 hover:bg-accent rounded-lg transition-colors"
              title="Refresh"
              data-testid="refresh-tickets-btn"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-xl placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="search-tickets-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-card border border-border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="filter-status-select"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Ticket List */}
            <div className="lg:col-span-1 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-12 bg-card rounded-xl border border-border">
                  <Ticket className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No tickets found</p>
                  <p className="text-sm text-muted-foreground">Issues you report will appear here</p>
                </div>
              ) : (
                filteredTickets.map((ticket) => {
                  const CategoryIcon = CATEGORY_ICONS[ticket.category] || HelpCircle;
                  const isSelected = selectedTicket?.ticket_id === ticket.ticket_id;
                  const hasNewResponse = ticket.responses?.length > 0 && 
                    ticket.responses[ticket.responses.length - 1]?.from === 'admin';
                  
                  return (
                    <button
                      key={ticket.ticket_id}
                      onClick={() => setSelectedTicket(ticket)}
                      data-testid={`ticket-item-${ticket.ticket_id}`}
                      className={`w-full text-left p-4 rounded-xl border transition-all ${
                        isSelected 
                          ? 'bg-blue-500/20 border-blue-500/50' 
                          : 'bg-card border-border hover:bg-accent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs font-mono text-muted-foreground">#{ticket.ticket_id}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {hasNewResponse && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full" />
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CONFIG[ticket.status]?.color} text-white`}>
                            {STATUS_CONFIG[ticket.status]?.label}
                          </span>
                        </div>
                      </div>
                      <h3 className="font-medium truncate">{ticket.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{formatDate(ticket.created_at)}</p>
                    </button>
                  );
                })
              )}
            </div>

            {/* Ticket Detail */}
            <div className="lg:col-span-2">
              {selectedTicket ? (
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  {/* Ticket Header */}
                  <div className="p-4 border-b border-border">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-mono text-muted-foreground">#{selectedTicket.ticket_id}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CONFIG[selectedTicket.status]?.color} text-white`}>
                            {STATUS_CONFIG[selectedTicket.status]?.label}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full bg-accent ${
                            selectedTicket.priority === 'critical' ? 'text-red-400' :
                            selectedTicket.priority === 'high' ? 'text-orange-400' :
                            selectedTicket.priority === 'medium' ? 'text-amber-400' : 'text-muted-foreground'
                          }`}>
                            {selectedTicket.priority}
                          </span>
                        </div>
                        <h2 className="text-xl font-semibold">{selectedTicket.title}</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          Created {formatDate(selectedTicket.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="p-4 border-b border-border">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Description</h3>
                    <p className="whitespace-pre-wrap">{selectedTicket.description}</p>
                    
                    {selectedTicket.error_message && (
                      <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <p className="text-xs font-medium text-red-400 mb-1">Error Details:</p>
                        <p className="text-sm font-mono">{selectedTicket.error_message}</p>
                      </div>
                    )}
                    
                    {selectedTicket.page_url && (
                      <p className="text-xs text-muted-foreground mt-2">Page: {selectedTicket.page_url}</p>
                    )}
                  </div>

                  {/* Conversation */}
                  <div className="p-4 border-b border-border max-h-64 overflow-y-auto">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Conversation ({selectedTicket.responses?.length || 0})
                    </h3>
                    
                    {selectedTicket.responses?.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No responses yet. Our team will review your ticket soon.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {selectedTicket.responses?.map((response) => (
                          <div 
                            key={response.id}
                            className={`p-3 rounded-lg ${
                              response.from === 'admin' 
                                ? 'bg-blue-500/10 border border-blue-500/30 ml-4' 
                                : 'bg-accent border border-border mr-4'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs font-medium ${
                                response.from === 'admin' ? 'text-blue-400' : 'text-muted-foreground'
                              }`}>
                                {response.from === 'admin' ? 'Support Team' : 'You'}
                              </span>
                              <span className="text-xs text-muted-foreground">{formatDate(response.created_at)}</span>
                            </div>
                            <p className="text-sm">{response.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Reply Box (if not closed) */}
                  {selectedTicket.status !== 'closed' && (
                    <div className="p-4">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newResponse}
                          onChange={(e) => setNewResponse(e.target.value)}
                          placeholder="Add a response..."
                          className="flex-1 px-4 py-2 bg-card border border-border rounded-xl placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onKeyDown={(e) => e.key === 'Enter' && handleSendResponse()}
                          disabled={submitting}
                          data-testid="ticket-response-input"
                        />
                        <button
                          onClick={handleSendResponse}
                          disabled={!newResponse.trim() || submitting}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          data-testid="send-response-btn"
                        >
                          {submitting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send size={16} />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Resolution Notes */}
                  {selectedTicket.resolution_notes && (
                    <div className="p-4 bg-emerald-500/10 border-t border-emerald-500/30">
                      <h3 className="text-sm font-medium text-emerald-400 mb-1">Resolution</h3>
                      <p className="text-sm">{selectedTicket.resolution_notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-card rounded-xl border border-border p-12 text-center">
                  <Ticket className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground">Select a ticket</h3>
                  <p className="text-sm text-muted-foreground">Click on a ticket to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default MyTickets;
